/**
 * useTransactionOptions - Hook para buscar opções das TABELAS DEDICADAS
 * 
 * Busca categorias, parceiros, fontes, etc. das tabelas de configuração
 * (categories, subcategories, business_partners, payment_methods, etc.)
 * e fornece funções para criar novas opções quando o usuário digitar no Combobox
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { toast } from 'sonner';

// =====================================================
// TIPOS
// =====================================================

interface Category {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  description?: string;
  entry_type: 'income' | 'expense';
  created_at?: string;
}

interface Subcategory {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  description?: string;
  main_category: string;
  created_at?: string;
}

interface BusinessPartner {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  description?: string;
  type: string;
  contact_info?: string;
  created_at?: string;
}

interface DocumentSource {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  description?: string;
  type: string;
  created_at?: string;
}

interface PaymentMethod {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  description?: string;
  method_type: string;
  created_at?: string;
}

interface CostCenter {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  description?: string;
  department?: string;
  created_at?: string;
}

interface Location {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  created_at?: string;
}

interface Tag {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  color?: string;
  created_at?: string;
}

interface BankAccount {
  id: string;
  company_id: string;
  user_id: string;
  account_name: string; // ✅ Corrigido: account_name em vez de name
  bank_id?: string;
  account_number?: string;
  currency: string;
  is_active?: boolean; // ✅ Adicionado: is_active
  created_at?: string;
}

interface Project {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  budget?: number;
  status?: string;
  created_at?: string;
}

// =====================================================
// HOOK PRINCIPAL
// =====================================================

export function useTransactionOptions(companyId: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // =====================================================
  // QUERIES - Buscar das tabelas dedicadas
  // =====================================================

  // Buscar categorias da tabela 'categories'
  const { data: categories = { mainCategories: [], categoriesByMain: {} }, isLoading: loadingCategories } = useQuery({
    queryKey: ['transaction-categories', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return { mainCategories: [], categoriesByMain: {} };

      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching categories:', error);
        return { mainCategories: [], categoriesByMain: {} };
      }

      // Agrupar por main_category (aqui o "name" da tabela categories é a main_category)
      const uniqueMainCategories = (data || []).map((c: Category) => c.name);

      // Para cada main_category, buscar suas subcategories
      const categoriesByMain: Record<string, { categories: Set<string>; subcategories: Set<string>; entry_type: string }> = {};
      for (const cat of (data || [])) {
        categoriesByMain[cat.name] = {
          categories: new Set<string>([cat.name]), // A própria categoria
            subcategories: new Set<string>(),
          entry_type: cat.entry_type,
          };
        }

      return {
        mainCategories: uniqueMainCategories,
        categoriesByMain,
      };
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar subcategorias da tabela 'subcategories'
  const { data: subcategories = [], isLoading: loadingSubcategories } = useQuery<Subcategory[]>({
    queryKey: ['transaction-subcategories', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching subcategories:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar parceiros comerciais da tabela 'business_partners'
  const { data: businessPartners = [], isLoading: loadingPartners } = useQuery<BusinessPartner[]>({
    queryKey: ['business-partners', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('business_partners')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching business partners:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar fontes de documentos da tabela 'document_sources'
  const { data: documentSources = [], isLoading: loadingSources } = useQuery<DocumentSource[]>({
    queryKey: ['document-sources', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('document_sources')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching document sources:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar métodos de pagamento da tabela 'payment_methods'
  const { data: paymentMethods = [], isLoading: loadingMethods } = useQuery<PaymentMethod[]>({
    queryKey: ['payment-methods', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching payment methods:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar contas bancárias da tabela 'bank_accounts'
  const { data: accounts = [], isLoading: loadingAccounts } = useQuery<BankAccount[]>({
    queryKey: ['accounts', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('bank_accounts')
        .select('*')
        .eq('company_id', companyId)
        .order('account_name'); // ✅ Corrigido: account_name em vez de name

      if (error) {
        console.error('[useTransactionOptions] Error fetching bank accounts:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar centros de custo da tabela 'cost_centers'
  const { data: costCenters = [], isLoading: loadingCostCenters } = useQuery<CostCenter[]>({
    queryKey: ['cost-centers', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('cost_centers')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching cost centers:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar stakeholders da tabela 'stakeholders'
  const { data: stakeholders = [], isLoading: loadingStakeholders } = useQuery<any[]>({
    queryKey: ['stakeholders', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('stakeholders')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching stakeholders:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar localizações da tabela 'locations'
  const { data: locations = [], isLoading: loadingLocations } = useQuery<Location[]>({
    queryKey: ['locations', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching locations:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar tags da tabela 'tags'
  const { data: tags = [], isLoading: loadingTags } = useQuery<Tag[]>({
    queryKey: ['tags', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching tags:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // Buscar projetos da tabela 'projects'
  const { data: projects = [], isLoading: loadingProjects } = useQuery<Project[]>({
    queryKey: ['projects', companyId, user?.id],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        console.error('[useTransactionOptions] Error fetching projects:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && !!companyId,
  });

  // =====================================================
  // MUTATIONS - Criar novas opções
  // =====================================================

  // Criar nova categoria
  const createCategoryMutation = useMutation({
    mutationFn: async ({ name, entryType }: { name: string; entryType: 'income' | 'expense' }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('categories')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
          entry_type: entryType,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transaction-categories', companyId] });
      toast.success('Categoria criada com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating category:', error);
      toast.error('Erro ao criar categoria');
    },
  });

  // Criar nova subcategoria
  const createSubcategoryMutation = useMutation({
    mutationFn: async ({ name, mainCategory }: { name: string; mainCategory: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('subcategories')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
          main_category: mainCategory,
          active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidar múltiplas variações da query key
      queryClient.invalidateQueries({ queryKey: ['transaction-subcategories'] });
      queryClient.invalidateQueries({ queryKey: ['subcategories'] });
      
      // Forçar refetch imediato
      queryClient.refetchQueries({ queryKey: ['transaction-subcategories', companyId] });
      
      toast.success('Subcategoria criada com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating subcategory:', error);
      toast.error('Erro ao criar subcategoria');
    },
  });

  // Criar novo parceiro comercial
  const createBusinessPartnerMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('business_partners')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
          type: 'other', // Valor em inglês (default correto)
          active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidar múltiplas variações da query key
      queryClient.invalidateQueries({ queryKey: ['business-partners'] });
      
      // Forçar refetch imediato
      queryClient.refetchQueries({ queryKey: ['business-partners', companyId] });
      
      toast.success('Parceiro criado com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating business partner:', error);
      toast.error('Erro ao criar parceiro');
    },
  });

  // Criar nova fonte de documento
  const createDocumentSourceMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('document_sources')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
          type: 'other', // Valor em inglês (default correto)
          active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidar múltiplas variações da query key
      queryClient.invalidateQueries({ queryKey: ['document-sources'] });
      
      // Forçar refetch imediato
      queryClient.refetchQueries({ queryKey: ['document-sources', companyId] });
      
      toast.success('Fonte de documento criada com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating document source:', error);
      toast.error('Erro ao criar fonte de documento');
    },
  });

  // Criar novo método de pagamento
  const createPaymentMethodMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('payment_methods')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
          type: 'other', // Valor em inglês
          method_type: 'outro', // Tipo padrão em português
          active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidar múltiplas variações da query key
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      
      // Forçar refetch imediato
      queryClient.refetchQueries({ queryKey: ['payment-methods', companyId] });
      
      toast.success('Método de pagamento criado com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating payment method:', error);
      toast.error('Erro ao criar método de pagamento');
    },
  });

  // Criar novo centro de custo
  const createCostCenterMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('cost_centers')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
          active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers', companyId] });
      toast.success('Centro de custo criado com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating cost center:', error);
      toast.error('Erro ao criar centro de custo');
    },
  });

  // Criar novo stakeholder
  const createStakeholderMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('stakeholders')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
          active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholders', companyId] });
      toast.success('Stakeholder criado com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating stakeholder:', error);
      toast.error('Erro ao criar stakeholder');
    },
  });

  // Criar nova localização
  const createLocationMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('locations')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations', companyId] });
      toast.success('Localização criada com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating location:', error);
      toast.error('Erro ao criar localização');
    },
  });

  // Criar nova tag
  const createTagMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('tags')
        .insert({
          company_id: companyId,
          user_id: user.id,
          name,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', companyId] });
      toast.success('Tag criada com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating tag:', error);
      toast.error('Erro ao criar tag');
    },
  });

  // Criar nova conta bancária
  const createAccountMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user?.id || !companyId) throw new Error('Missing user or company');

      const { data, error } = await supabase
        .from('bank_accounts')
        .insert({
          company_id: companyId,
          user_id: user.id,
          account_name: name, // ✅ Corrigido: account_name em vez de name
          account_type: 'checking', // Tipo padrão: conta corrente
          initial_balance: 0,
          current_balance: 0,
          is_active: true, // ✅ Corrigido: is_active em vez de active
        })
        .select()
        .single();

      if (error) {
        console.error('[useTransactionOptions] Error details:', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', companyId] });
      toast.success('Conta bancária criada com sucesso!');
    },
    onError: (error) => {
      console.error('[useTransactionOptions] Error creating bank account:', error);
      toast.error('Erro ao criar conta bancária');
    },
  });

  // =====================================================
  // RETORNO
  // =====================================================

  // Organizar subcategorias por main_category para compatibilidade com o formulário
  const organizedCategories = {
    mainCategories: categories.mainCategories,
    categoriesByMain: { ...categories.categoriesByMain },
  };

  // Adicionar subcategorias da tabela às categorias principais
  subcategories.forEach((sub) => {
    if (!organizedCategories.categoriesByMain[sub.main_category]) {
      organizedCategories.categoriesByMain[sub.main_category] = {
        categories: new Set<string>(),
        subcategories: new Set<string>(),
        entry_type: 'expense',
      };
    }
    organizedCategories.categoriesByMain[sub.main_category].subcategories.add(sub.name);
  });

  return {
    // Dados
    categories: organizedCategories,
    subcategories,
    businessPartners,
    documentSources,
    paymentMethods,
    accounts,
    costCenters,
    stakeholders,
    locations,
    tags,
    projects,
    
    // Loading
    isLoading:
      loadingCategories ||
      loadingSubcategories ||
      loadingPartners ||
      loadingSources ||
      loadingMethods ||
      loadingAccounts ||
      loadingCostCenters ||
      loadingStakeholders ||
      loadingLocations ||
      loadingTags ||
      loadingProjects,
    
    // Mutations para criar novas opções
    createCategory: createCategoryMutation.mutate,
    createSubcategory: createSubcategoryMutation.mutate,
    createBusinessPartner: createBusinessPartnerMutation.mutate,
    createDocumentSource: createDocumentSourceMutation.mutate,
    createPaymentMethod: createPaymentMethodMutation.mutate,
    createCostCenter: createCostCenterMutation.mutate,
    createStakeholder: createStakeholderMutation.mutate,
    createLocation: createLocationMutation.mutate,
    createTag: createTagMutation.mutate,
    createAccount: createAccountMutation.mutate,
    
    // Loading states das mutations
    isCreating:
      createCategoryMutation.isPending ||
      createSubcategoryMutation.isPending ||
      createBusinessPartnerMutation.isPending ||
      createDocumentSourceMutation.isPending ||
      createPaymentMethodMutation.isPending ||
      createCostCenterMutation.isPending ||
      createStakeholderMutation.isPending ||
      createLocationMutation.isPending ||
      createTagMutation.isPending ||
      createAccountMutation.isPending,
  };
}
